import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    FaChevronDown,
    FaChevronRight,
    FaEdit,
    FaPlus
} from "react-icons/fa";
import config from "../../config";
import {
    Button,
    Container,
    Row,
    Col,
    Card,
    Form,
    Modal,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";
import Layout from "../layouts/Main";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PulseLoader } from "react-spinners";


const WarehouseLocationNode = ({ location, onAddSubLocation, onEdit, level, refreshSignal }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [children, setChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchChildren = useCallback(async () => {
        if (location.hasChildren) {
            setIsLoading(true);
            try {
                const res = await axios.get(`${config.API_BASE_URL}/new-warehouses`, {
                    params: { parentId: location._id }
                });
                setChildren(res.data);
            } catch (err) {
                toast.error("Failed to fetch child locations");
            } finally {
                setIsLoading(false);
            }
        }
    },[location._id, location.hasChildren]);

    const handleToggleExpand = async () => {
        if (!isExpanded && location.hasChildren) {
            await fetchChildren();
        }
        setIsExpanded(!isExpanded);
    };

    // When refreshSignal changes, reload children if expanded
    useEffect(() => {
        if (isExpanded) {
            fetchChildren();
        }
    }, [refreshSignal, isExpanded, fetchChildren]);

    return (
        <div style={{ marginLeft: `${level * 20}px`, marginTop: "8px" }}>
            <div className="d-flex align-items-center justify-content-between py-1 px-2 border rounded bg-light">
                <div className="d-flex align-items-center">
                    {location.hasChildren && (
                        <span
                            onClick={handleToggleExpand}
                            style={{ cursor: "pointer", marginRight: "6px" }}
                        >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                    )}
                    <strong className="me-2">{location.label}</strong>
                    {location.isStorage && <span className="badge bg-info">Storage</span>}
                </div>
                <div>
                    {!location.isStorage && (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Add Sub-location</Tooltip>}>
                            <Button
                                variant="outline-success"
                                size="sm"
                                className="me-1"
                                onClick={() => onAddSubLocation(location._id)}
                            >
                                <FaPlus />
                            </Button>
                        </OverlayTrigger>
                    )}
                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                        <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => onEdit(location)}
                        >
                            <FaEdit />
                        </Button>
                    </OverlayTrigger>
                </div>
            </div>

            {isExpanded &&
                (isLoading ? (
                    <PulseLoader
                        color="#014b7cbb"
                        size={9}
                    />
                ) : (
                    children.map((child) => (
                        <WarehouseLocationNode
                            key={child._id}
                            location={child}
                            onAddSubLocation={onAddSubLocation}
                            onEdit={onEdit}
                            level={level + 1}
                            refreshSignal={refreshSignal}
                        />
                    ))
                ))}
        </div>
    );
};

export default function WarehouseLocationTree() {
    const [topLevelLocations, setTopLevelLocations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [parentId, setParentId] = useState(null);
    const [editId, setEditId] = useState(null);
    const [label, setLabel] = useState("");
    const [isStorage, setIsStorage] = useState(false);
    const [hasChildren, setHasChildren] = useState(false);
    const [refreshSignal, setRefreshSignal] = useState(Date.now());
    const [loadingTopLevel, setLoadingTopLevel] = useState(false);

    useEffect(() => {
        fetchTopLevel();
    }, []);

    const fetchTopLevel = async () => {
        setLoadingTopLevel(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/new-warehouses`);
            setTopLevelLocations(res.data);
        } catch (err) {
            toast.error("Failed to fetch locations");
        } finally {
            setLoadingTopLevel(false);
        }
    };

    const openAddModal = (pid = null) => {
        setParentId(pid);
        setEditId(null);
        setLabel("");
        setIsStorage(false);
        setHasChildren(false);
        setShowModal(true);
    };

    const openEditModal = async (location) => {
        try {
            const res = await axios.get(`${config.API_BASE_URL}/new-warehouses`, {
                params: { parentId: location._id }
            });
            setHasChildren(res.data.length > 0);
        } catch {
            setHasChildren(false);
        }
        setEditId(location._id);
        setParentId(location.parent || null);
        setLabel(location.label);
        setIsStorage(location.isStorage);
        setShowModal(true);
    };

    const saveLocation = async () => {
        try {
            if (editId) {
                await axios.put(`${config.API_BASE_URL}/new-warehouses/${editId}`, {
                    label,
                    isStorage
                });
                toast.success("Location updated successfully");
            } else {
                await axios.post(`${config.API_BASE_URL}/new-warehouses`, {
                    label,
                    parentId,
                    isStorage
                });
                toast.success("Location added successfully");
            }
            setShowModal(false);

            // Trigger a refresh
            setRefreshSignal(Date.now());

            // If a new top-level node is added, also refresh root
            if (!parentId) fetchTopLevel();

        } catch (err) {
            toast.error(err.response?.data?.message || "Error saving location");
        }
    };

    return (
        <Layout>
            <Container fluid>
                <Row className="my-3">
                    <Col className="d-flex justify-content-between align-items-center">
                        <h2 className="mb-0">Warehouse Locations</h2>
                        <Button variant="primary" size="sm" onClick={() => openAddModal()}>
                            <FaPlus className="me-1" /> Add Top-Level Location
                        </Button>
                    </Col>
                </Row>

                <Row>
                    {loadingTopLevel ? (
                        <PulseLoader
                            color="#014b7cbb"
                            size={9}
                        />
                    ) : topLevelLocations.length > 0 ? (
                        topLevelLocations.map((loc) => (
                            <Col xs={12} key={loc._id} className="mb-2">
                                <Card>
                                    <Card.Body className="p-2">
                                        <WarehouseLocationNode
                                            location={loc}
                                            onAddSubLocation={openAddModal}
                                            onEdit={openEditModal}
                                            level={0}
                                            refreshSignal={refreshSignal}
                                        />
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))
                    ) : (
                        <Col><p className="text-muted">No locations found.</p></Col>
                    )}
                </Row>

                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editId ? "Edit Location" : "Add Location"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Location Label</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={label}
                                    onChange={(e) => setLabel(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        hasChildren
                                            ? <Tooltip>Cannot set as storage because this location has sub-locations</Tooltip>
                                            : <></>
                                    }
                                >
                                    <div>
                                        <Form.Check
                                            type="checkbox"
                                            label="Is Storage"
                                            checked={isStorage}
                                            disabled={hasChildren}
                                            onChange={(e) => setIsStorage(e.target.checked)}
                                        />
                                    </div>
                                </OverlayTrigger>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" size="sm" onClick={saveLocation}>
                            Save
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </Layout>
    );
}
